/**
 * Aporte al total de facturación del mes / expediente en Materialidad.
 * Objetivo: cuánto facturó (para umbral de riesgo), no saldos ni cobranza.
 * - Facturas I: suman
 * - Notas de crédito E: restan
 * - Complementos P: 0 (informativos; van aparte si son a años previos)
 * - Cancelados: se omiten en el sumatorio del listado
 */
export function cfdiAmountForNetTotal(tipoComprobante, total, { priorYearInvoice = false } = {}) {
  const amount = Math.abs(Number(total) || 0);
  const tipo = String(tipoComprobante || "").trim().toUpperCase();
  // priorYearInvoice se conserva por compatibilidad; P nunca entra al total de facturación.
  void priorYearInvoice;
  if (tipo === "I") return amount;
  if (tipo === "E") return -amount;
  return 0;
}

/** CFDI cancelados no entran al detalle ni al total de facturación. */
export function isCfdiCancelledForMaterialidad(row) {
  if (!row || typeof row !== "object") return false;
  const keys = ["estatus", "estado", "sat_verify_estado", "estatus_sat"];
  for (const key of keys) {
    const val = String(row[key] || "").trim().toLowerCase();
    if (val === "cancelado" || val.startsWith("cancelado")) return true;
  }
  return false;
}

/** Solo complementos P ligados a facturas de años anteriores (nunca facturas I). */
export function isPriorYearPayment(row) {
  return Boolean(row?.prior_year_invoice) && String(row?.tipo_comprobante || "").toUpperCase() === "P";
}

/** NC (E) vinculada a factura(s) de año(s) anterior(es). */
export function isPriorYearCreditNote(row) {
  if (String(row?.tipo_comprobante || "").toUpperCase() !== "E") return false;
  if (row?.prior_year_credit_note) return true;
  const years = row?.prior_year_credit_note_years || row?.related_invoice_years || [];
  if (!Array.isArray(years) || !years.length) return false;
  const periodKey = String(row?.period_key || "").slice(0, 4);
  const periodYear = Number(periodKey);
  if (!Number.isFinite(periodYear) || periodYear < 2000) return false;
  return years.some((y) => Number(y) < periodYear);
}

export function sumCfdiNetTotal(rows) {
  return (rows || []).reduce((acc, r) => {
    if (isCfdiCancelledForMaterialidad(r)) return acc;
    return acc + cfdiAmountForNetTotal(r?.tipo_comprobante, r?.total, {
      priorYearInvoice: isPriorYearPayment(r),
    });
  }, 0);
}

/**
 * Suma solo ImpPagado de P a facturas de años previos.
 * No incluye facturas I aunque tengan nota o flag erróneo (evita dobleteo).
 */
export function sumPriorYearPayments(rows) {
  return (rows || []).reduce((acc, r) => {
    if (isCfdiCancelledForMaterialidad(r)) return acc;
    if (!isPriorYearPayment(r)) return acc;
    const dedicated = Number(r?.prior_year_payment_mxn);
    if (Number.isFinite(dedicated) && Math.abs(dedicated) >= 0.005) {
      return acc + Math.abs(dedicated);
    }
    return acc + Math.abs(Number(r?.total) || 0);
  }, 0);
}

/** Filas vigentes para el detalle por mes (sin canceladas). */
export function filterActiveCfdiRows(rows) {
  return (rows || []).filter((r) => !isCfdiCancelledForMaterialidad(r));
}

/**
 * Vista alineada al filtro de riesgo del listado.
 * Riesgo Alto: muestra I, P (incl. pagos a años previos con leyenda) y NC del
 * mismo ejercicio. Solo oculta NC (E) ligadas a facturas de años anteriores.
 */
export function filterCfdiRowsForRiskView(rows, riskFilter) {
  const active = filterActiveCfdiRows(rows);
  const risk = String(riskFilter || "").trim().toLowerCase();
  if (risk === "alto") {
    return active.filter((r) => {
      const t = String(r?.tipo_comprobante || "").trim().toUpperCase();
      if (t === "E" && isPriorYearCreditNote(r)) return false;
      return true;
    });
  }
  return active;
}

/** El bloque de pagos a años anteriores se muestra en todos los filtros de riesgo. */
export function showPriorYearPaymentsForRiskFilter(_riskFilter) {
  return true;
}
